"use client";

import { useState, useEffect } from "react";
import NearbySectionClient from "@/components/maps/NearbySectionClient";

type NearbyPin = {
  id: string;
  titolo: string;
  lat: number;
  lng: number;
  date_from?: string;
  date_to?: string;
  slug: string;
  year?: number;
  url?: string;
  distance_km?: number;
};

export default function ClientVicinoAMe({
  initialCenter,
  initialToday,
}: {
  initialCenter: { lat: number; lng: number } | null;
  initialToday: NearbyPin[];
}) {
  const [userPosition, setUserPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [showLocationButton, setShowLocationButton] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<string>("");

  // Pre-flight permission check
  async function checkGeoPermission(): Promise<"granted" | "denied" | "prompt"> {
    if ("permissions" in navigator && (navigator as any).permissions?.query) {
      try {
        const st = await (navigator as any).permissions.query({
          name: "geolocation" as PermissionName,
        });
        return st.state;
      } catch (e) {
        return "prompt";
      }
    }
    return "prompt";
  }

  // Fast-then-precise location request flow
  async function requestLocationFastThenPrecise() {
    if (!navigator.geolocation) {
      setError("Geolocalizzazione non supportata.");
      setIsLoading(false);
      return;
    }

    setStatus("Cerco una posizione rapida…");
    setIsLoading(true);
    setError("");

    let fastPosObtained = false;

    // Step 1: Get fast location (low accuracy, cached if available)
    await new Promise<void>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          const newPos = { lat: p.coords.latitude, lng: p.coords.longitude };
          setUserPosition(newPos);
          fastPosObtained = true;
          // Salva immediatamente per UI responsive
          try {
            localStorage.setItem(
              "geo_cache",
              JSON.stringify({ lat: newPos.lat, lng: newPos.lng, ts: Date.now() })
            );
          } catch (e) {
            // Ignora errori localStorage
          }
          resolve();
        },
        () => {
          // Se il primo tentativo fallisce, continua comunque al passo 2
          resolve();
        },
        { enableHighAccuracy: false, maximumAge: 600000, timeout: 4000 }
      );
    });

    // Step 2: Refine with high accuracy
    setStatus("Affino la posizione…");

    const watchId = navigator.geolocation.watchPosition(
      (p) => {
        const newPos = { lat: p.coords.latitude, lng: p.coords.longitude };
        setUserPosition(newPos);
        setIsLoading(false);
        setStatus("");
        navigator.geolocation.clearWatch(watchId);

        // Salva in localStorage
        try {
          localStorage.setItem(
            "geo_cache",
            JSON.stringify({ lat: newPos.lat, lng: newPos.lng, ts: Date.now() })
          );
        } catch (e) {
          // Ignora errori localStorage
        }
      },
      (err) => {
        navigator.geolocation.clearWatch(watchId);
        setIsLoading(false);

        if (err.code === 1) {
          setError("Permesso negato. Apri Impostazioni > Privacy > Localizzazione.");
          setStatus("");
        } else {
          // Se abbiamo già una posizione dal passo 1, non mostrare errore
          if (!fastPosObtained) {
            setError("Errore nel recupero della posizione.");
            setStatus("");
          } else {
            setStatus("");
          }
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  }

  // Leggi parametri URL e imposta posizione automaticamente
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const lat = params.get("lat");
      const lng = params.get("lng");

      if (lat && lng) {
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        if (!isNaN(latNum) && !isNaN(lngNum)) {
          setUserPosition({ lat: latNum, lng: lngNum });
          setStatus("");
          // Salva in localStorage
          try {
            localStorage.setItem(
              "geo_cache",
              JSON.stringify({ lat: latNum, lng: lngNum, ts: Date.now() })
            );
          } catch (e) {
            // Ignora errori localStorage
          }
          return;
        }
      }

      // Controlla localStorage cache (15 minuti TTL)
      let hasCached = false;
      try {
        const cached = JSON.parse(localStorage.getItem("geo_cache") || "null");
        const TTL = 15 * 60 * 1000; // 15 minutes
        if (cached && cached.lat && cached.lng && Date.now() - cached.ts < TTL) {
          setUserPosition({ lat: cached.lat, lng: cached.lng });
          setStatus("");
          hasCached = true;
        }
      } catch (e) {
        // Ignora errori localStorage, procedi con geolocalizzazione
      }

      // Controlla permesso e agisci di conseguenza
      checkGeoPermission().then((perm) => {
        if (perm === "granted") {
          if (!hasCached) {
            // Permission già concessa ma nessuna cache, richiedi posizione automaticamente
            requestLocationFastThenPrecise();
          } else {
            // Update location in background se abbiamo cache
            requestLocationFastThenPrecise();
          }
        } else if (perm === "prompt") {
          // Mostra button CTA solo se non abbiamo cache
          if (!hasCached) {
            setShowLocationButton(true);
          }
        } else {
          // denied
          if (!hasCached) {
            setError("Attiva la posizione nelle Impostazioni.");
          }
        }
      });
    }
  }, []);

  // Status feedback per screen reader
  const statusContent = status ? (
    <div aria-live="polite" className="sr-only" id="status-region">
      {status}
    </div>
  ) : null;

  // Se non c'è posizione, mostra solo il bottone
  if (!userPosition) {
    return (
      <>
        {statusContent}
        <div className="text-center py-8">
          {error && (
            <p className="text-sm text-rose-700 mb-6" role="alert">
              {error}
            </p>
          )}
          {showLocationButton && (
            <button
              type="button"
              onClick={() => {
                setShowLocationButton(false);
                requestLocationFastThenPrecise();
              }}
              className="cta-button"
              aria-label="Usa la mia posizione per trovare sagre vicine"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ display: "inline-block", verticalAlign: "middle" }}
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>Usa la mia posizione</span>
            </button>
          )}
        </div>
      </>
    );
  }

  // Quando c'è posizione, mostra la mappa
  return (
    <>
      {statusContent}
      <NearbySectionClient
        center={userPosition}
        radiusKm={100}
        daysAhead={60}
        listLimit={12}
        title="Sagre nelle vicinanze"
        subtitle="Entro 100 km nei prossimi 60 giorni"
        hideHeader={true}
      />
    </>
  );
}

